import React, { useState, useEffect } from "react";
import { FiLoader, FiSearch, FiCheck, FiAlertCircle } from "react-icons/fi";
import type {
  CreateTenantData,
  OrganizationData,
} from "../../../shared/types/tenant";
import { TenantType } from "../../../constants/enum";
import defaultIndustries from "../../../constants/defaultIndustries";
import { websiteOrigination } from "../../../shared/services/apis/tenant";
import Dialog from "../../../shared/ui/dialog";
import { CiSearch } from "react-icons/ci";
import LoadingOverlay from "../../../shared/ui/LoadingOverlay";
import DocumentUploadModal from "./DocumentUploadModal";
import { useBrand } from "../../../context/brandContext";

interface TenantModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (
    data: CreateTenantData
  ) => Promise<{ success: boolean; tenantId?: string; tenantname?: string }>;
  brandId: string;
  isLoading?: boolean;
}

const TenantModal: React.FC<TenantModalProps> = ({
  isOpen,
  onClose,
  onSave,
  brandId,
  isLoading = false,
}) => {
  const [step, setStep] = useState<"search" | "confirm">("search");
  const [domain, setDomain] = useState("");
  const [selectedTenantType, setSelectedTenantType] =
    useState<keyof typeof TenantType>("NON_REAL_ESTATE");
  const [selectedIndustries, setSelectedIndustries] = useState<string[]>([]);
  const [organizationData, setOrganizationData] =
    useState<OrganizationData | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [formData, setFormData] = useState<CreateTenantData>({
    name: "",
    brandId: brandId,
    isActive: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isUploadModalOpen, setIsUploadModalOpen] = useState(false);

  const [tenantId, setTenantId] = useState<string | null | undefined>(null);
  const [tenantName, setTenantName] = useState<string | null | undefined>(null);
  const [isCreating, setIsCreating] = useState(false);
    const { refreshBrand } = useBrand();

  useEffect(() => {
    setDomain("");
    if (isOpen) {
      setStep("search");
      setSelectedTenantType("NON_REAL_ESTATE");
      setSelectedIndustries([]);
      setOrganizationData(null);
      setFormData({
        name: "",
        brandId: brandId,
        isActive: true,
      });
      setErrors({});
      setSearchError(null);
    }
  }, [isOpen, brandId]);

  const handleDomainSearch = async () => {
    if (!domain.trim()) {
      setSearchError("Please enter a domain");
      return;
    }

    try {
      setIsSearching(true);
      setSearchError(null);

      const result = await websiteOrigination({
        website: domain.trim(),
      });

      if (!result.success) {
        setSearchError("Failed to retrieve organization data");
        return;
      }

      if (!result.apolloOrganizationId) {
        setSearchError(
          "Organization not found in database or Already exists. Please try a different domain."
        );
        return;
      }

      setOrganizationData(result);

      // Pre-populate form with organization data
      setFormData((prev) => ({
        ...prev,
        name: result.name, // Use domain as default name
        website: result.website,

        logoUrl: result.logoUrl || "",
        shortDescription: result.shortDescription || "",
        industries: result.industries || "",
        apolloOrganizationId: result.apolloOrganizationId || "",
      }));

      setStep("confirm");
    } catch (error) {
      console.error("Error searching domain:", error);
      setSearchError("Failed to search domain. Please try again.");
    } finally {
      setIsSearching(false);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = "Tenant name is required";
    } else if (formData.name.trim().length < 2) {
      newErrors.name = "Tenant name must be at least 2 characters";
    }

    // For REAL_ESTATE type, description is required
    // if (selectedTenantType === "REAL_ESTATE") {
    //   if (!formData.shortDescription?.trim()) {
    //     newErrors.shortDescription =
    //       "Description is required for Real Estate tenants";
    //   }
    // }

    if (!brandId) {
      newErrors.brandId = "Brand ID is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleManualCreate = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setIsCreating(true);
      const tenantData: CreateTenantData = {
        ...formData,
        tenantType: selectedTenantType,
        industries:
          selectedIndustries.length > 0 ? selectedIndustries : undefined,
      };
      const response = await onSave(tenantData);
      if (response.success) {
        setTenantId(response.tenantId);
        setIsUploadModalOpen(true);
        setTenantName(response.tenantname);
      }
    } catch (error) {
      console.error("Error creating tenant:", error);
    } finally {
      setIsCreating(false);
    }
  };

  const handleConfirmCreate = async () => {
    if (!validateForm()) {
      return;
    }
    console.log("Form data before saving:", organizationData);

    try {
      setIsCreating(true);
      const tenantData: CreateTenantData = {
        ...formData,
        tenantType: selectedTenantType,
        industries:
          selectedIndustries.length > 0 ? selectedIndustries : undefined,
        ...(organizationData && {
          website: organizationData.website,
          logoUrl: organizationData.logoUrl ?? undefined,
          shortDescription: organizationData.shortDescription ?? undefined,
          industries:
            organizationData.industries ??
            (selectedIndustries.length > 0 ? selectedIndustries : undefined),
          apolloOrganizationId:
            organizationData.apolloOrganizationId ?? undefined,
        }),
      };

      const response = await onSave(tenantData);
      if (response.success) {
        setTenantId(response.tenantId);
        setTenantName(response.tenantname);
        setIsUploadModalOpen(true);
      }

      console.log("tenentid", tenantId, "tenentname", tenantName);
    } catch (error) {
      console.error("Error creating tenant:", error);
    } finally {
      setTimeout(() => {
        setIsCreating(false);
      }, 5000);
    }
  };

  const handleInputChange = (
    field: keyof CreateTenantData,
    value: string | boolean
  ) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handleRemoveIndustry = (industryToRemove: string) => {
    setSelectedIndustries((prev) =>
      prev.filter((industry) => industry !== industryToRemove)
    );
  };

  const handleAddIndustry = (industryToAdd: string) => {
    if (industryToAdd && !selectedIndustries.includes(industryToAdd)) {
      setSelectedIndustries((prev) => [...prev, industryToAdd]);
    }
  };

  if (!isOpen) return null;
  return (
    <>
      {/* isDocumentUploadModalOpen */}
      {tenantId && tenantName && isUploadModalOpen && (
        <DocumentUploadModal
          isOpen={isUploadModalOpen}
          onClose={() => {setIsUploadModalOpen(false),refreshBrand()}}
          tenantId={tenantId}
          tenantName={tenantName}
          onUploadSuccess={() => { }}
        />
      )}
      {!isUploadModalOpen && (
        <Dialog
          isOpen={isOpen}
          onClose={onClose}
          title="Create a Company Report"
        >
          <div
            className={`${isUploadModalOpen ? " opacity-0" : "opacity-100"}`}
          >
            {step === "search" && (
              <>
                {/* Tenant Type Selection */}
                <div className="space-y-4">
                  <div>
                    <fieldset>
                      <legend className="block text-sm font-medium text-gray-700 mb-2">
                        Tenant Type
                      </legend>
                      <div className="flex gap-3">
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="tenantType"
                            value="NON_REAL_ESTATE"
                            checked={selectedTenantType === "NON_REAL_ESTATE"}
                            onChange={(e) =>
                              setSelectedTenantType(
                                e.target.value as keyof typeof TenantType
                              )
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            Non Real Estate
                          </span>
                        </label>
                        <label className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="tenantType"
                            value="REAL_ESTATE"
                            checked={selectedTenantType === "REAL_ESTATE"}
                            onChange={(e) =>
                              setSelectedTenantType(
                                e.target.value as keyof typeof TenantType
                              )
                            }
                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
                          />
                          <span className="text-sm text-gray-700">
                            Real Estate
                          </span>
                        </label>
                      </div>
                    </fieldset>
                  </div>



                  {selectedTenantType === "NON_REAL_ESTATE" ? (
                    <>
                      {/* Domain Search for Non Real Estate */}
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Enter Company Domain
                        </label>
                        <div className="flex items-center border border-gray-300 rounded-xl px-3 py-2 w-full">
                          <input
                            type="text"
                            value={domain}
                            onChange={(e) => setDomain(e.target.value)}
                            placeholder="Enter domain (e.g., apple.com)"
                            className="w-full text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-0"
                            disabled={isSearching}
                            onKeyDown={(e) =>
                              e.key === "Enter" && handleDomainSearch()
                            }
                          />
                          <CiSearch className="h-5 w-5 mr-2 text-gray-400" />
                        </div>
                      </div>
                      <div className="flex flex-col justify-end w-full px-4 rounded-lg">
                        <div className="flex items-end justify-end">
                          <button
                            onClick={handleDomainSearch}
                            disabled={isSearching || !domain.trim()}
                            className="px-6 py-4 bg-black hover:scale-105 disabled:bg-gray-300 text-white font-medium rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
                          >
                            {isSearching ? (
                              <>
                                <FiLoader className="w-4 h-4 animate-spin" />
                                <span>Searching...</span>
                              </>
                            ) : (
                              <>
                                <FiSearch className="w-4 h-4" />
                                <span>Search Company</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      {/* Manual Form for Real Estate */}
                      <div>
                        <label
                          htmlFor="tenantName"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Tenant Name <span className="text-red-500">*</span>
                        </label>
                        <input
                          id="tenantName"
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.name
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                            }`}
                          placeholder="Enter tenant name"
                          disabled={isLoading}
                        />
                        {errors.name && (
                          <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                            <FiAlertCircle className="w-4 h-4" />
                            {errors.name}
                          </p>
                        )}
                      </div>

                      <div>
                        <label
                          htmlFor="tenantDescription"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Description
                          {' '}
                          <small>
                            (Optional)

                          </small>


                        </label>
                        <textarea
                          id="tenantDescription"
                          value={formData.shortDescription || ""}
                          onChange={(e) =>
                            handleInputChange(
                              "shortDescription",
                              e.target.value
                            )
                          }
                          rows={3}
                          className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.shortDescription
                              ? "border-red-300 bg-red-50"
                              : "border-gray-300"
                            }`}
                          placeholder="Enter tenant description"
                          disabled={isLoading}
                        />
                        {errors.shortDescription && (
                          <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                            <FiAlertCircle className="w-4 h-4" />
                            {errors.shortDescription}
                          </p>
                        )}
                      </div>

                      <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div>
                          <span className="text-sm font-medium text-gray-700">
                            Active Status
                          </span>
                          <p className="text-xs text-gray-500">
                            Enable tenant after creation
                          </p>
                        </div>
                        <label
                          htmlFor="activeStatusRealEstate"
                          className="relative inline-flex items-center cursor-pointer"
                        >
                          <span className="sr-only">Toggle active status</span>
                          <input
                            id="activeStatusRealEstate"
                            type="checkbox"
                            checked={formData.isActive || false}
                            onChange={(e) =>
                              handleInputChange("isActive", e.target.checked)
                            }
                            className="sr-only peer"
                            disabled={isLoading}
                          />
                          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                        </label>
                      </div>

                      <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                        <button
                          onClick={onClose}
                          disabled={isLoading}
                          className="px-6 py-4 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleManualCreate}
                          disabled={isLoading}
                          className="px-6 py-4 bg-gray-600 hover:bg-black text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                        >
                          {isLoading ? (
                            <>
                              <FiLoader className="w-4 h-4 animate-spin" />
                              <span>Creating...</span>
                            </>
                          ) : (
                            <span>Create Report</span>
                          )}
                        </button>
                      </div>
                    </>
                  )}

                  {searchError && selectedTenantType === "NON_REAL_ESTATE" && (
                    <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                      <div className="flex gap-3">
                        <FiAlertCircle className="w-5 h-5 text-red-500 flex-shrink-0" />
                        <div>
                          <h4 className="text-sm font-medium text-red-800">
                            Error
                          </h4>
                          <p className="text-sm text-red-700 mt-1">
                            {searchError}
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}

            {step === "confirm" && organizationData && (
              <>
                <div className="bg-[#FDFDFD]  rounded-xl p-4">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="bg-gray-100 p-1.5 rounded-full">
                      <FiCheck className="w-4 h-4 text-gray-600" />
                    </div>
                    <div>
                      <h3 className="font-medium text-gray-900">
                        Organization Found
                      </h3>
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    {organizationData.shortDescription && (
                      <div className="col-span-2 bg-white rounded-lg p-3">
                        <p className="text-xs text-gray-500 mb-1">
                          Description
                        </p>
                        <p className="text-sm text-gray-900">
                          {organizationData.shortDescription}
                        </p>
                      </div>
                    )}
                  </div>
                </div>

                {/* Form Fields */}
                <div className="space-y-4">
                  <div>
                    <label
                      htmlFor="confirmTenantName"
                      className="block text-sm font-medium text-gray-700 mt-2 mb-2"
                    >
                      Tenant Name
                    </label>
                    <input
                      id="confirmTenantName"
                      type="text"
                      value={formData.name}
                      onChange={(e) =>
                        handleInputChange("name", e.target.value)
                      }
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${errors.name
                          ? "border-red-300 bg-red-50"
                          : "border-gray-300"
                        }`}
                      placeholder="Enter tenant name"
                      disabled={isLoading}
                    />
                    {errors.name && (
                      <p className="text-red-600 text-sm mt-1 flex items-center gap-1">
                        <FiAlertCircle className="w-4 h-4" />
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Industry Selection for Confirm Step */}

                  <div>
                    <label
                      htmlFor="industry-search-confirm"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Industries{" "}
                      <span className="text-gray-400 text-xs">(Optional)</span>
                    </label>
                    <div className="space-y-3">
                      {/* Selected Industries */}
                      {selectedIndustries.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {selectedIndustries.map((industry) => (
                            <span
                              key={industry}
                              className="inline-flex items-center gap-1 bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
                            >
                              {industry}
                              <button
                                type="button"
                                onClick={() => handleRemoveIndustry(industry)}
                                className="text-blue-600 hover:text-blue-800"
                                aria-label={`Remove ${industry}`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Industry Dropdown */}
                      <select
                        id="industry-search-confirm"
                        value=""
                        onChange={(e) => handleAddIndustry(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors"
                        disabled={isLoading}
                      >
                        <option value="">Select an industry to add...</option>
                        {defaultIndustries
                          .filter(
                            (industry) => !selectedIndustries.includes(industry)
                          )
                          .map((industry) => (
                            <option key={industry} value={industry}>
                              {industry}
                            </option>
                          ))}
                      </select>
                    </div>
                  </div>

                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div>
                      <span className="text-sm font-medium text-gray-700">
                        Active Status
                      </span>
                      <p className="text-xs text-gray-500">
                        Enable tenant after creation
                      </p>
                    </div>
                    <label
                      htmlFor="confirmActiveStatus"
                      className="relative inline-flex items-center cursor-pointer"
                    >
                      <span className="sr-only">Toggle active status</span>
                      <input
                        id="confirmActiveStatus"
                        type="checkbox"
                        checked={formData.isActive || false}
                        onChange={(e) =>
                          handleInputChange("isActive", e.target.checked)
                        }
                        className="sr-only peer"
                        disabled={isLoading}
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center justify-end gap-3 pt-4 border-t border-gray-200">
                  <button
                    onClick={onClose}
                    disabled={isLoading}
                    className="px-6 py-4 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors disabled:opacity-50 cursor-pointer"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleConfirmCreate}
                    disabled={isLoading}
                    className="px-6 py-4 bg-gray-600 hover:bg-black text-white rounded-lg transition-colors disabled:opacity-50 flex items-center gap-2 cursor-pointer"
                  >
                    {isLoading ? (
                      <>
                        <FiLoader className="w-4 h-4 animate-spin" />
                        <span>Creating...</span>
                      </>
                    ) : (
                      <span>Create Report</span>
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </Dialog>
      )}
      {isCreating && <LoadingOverlay onMinimize={onClose} />}
    </>
  );
};

export default TenantModal;
